/**
 * Authentication State Manager
 * Prevents random logouts by managing authentication state more persistently
 */

class AuthStateManager {
  private static instance: AuthStateManager;
  private authState: { authenticated: boolean; lastCheck: number } | null = null;
  private readonly CACHE_DURATION = 60000; // 60 seconds - longer cache for stability

  private constructor() {
    this.loadAuthState();
    this.setupEventListeners();
  }

  static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }

  private loadAuthState(): void {
    try {
      const stored = sessionStorage.getItem('auth_state_cache');
      if (stored) {
        this.authState = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading auth state:', error);
    }
  }

  private saveAuthState(): void {
    try {
      if (this.authState) {
        sessionStorage.setItem('auth_state_cache', JSON.stringify(this.authState));
      }
    } catch (error) {
      console.error('Error saving auth state:', error);
    }
  }

  private setupEventListeners(): void {
    // Clear cache when storage changes (logout in another tab)
    window.addEventListener('storage', (e) => {
      if (e.key === 'jwt_access_token' && !e.newValue) {
        console.log('🔄 Auth token removed in another tab, clearing cache');
        this.clearCache();
      }
    });

    // Don't clear cache on visibility change - keep user logged in
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('🔄 Tab became visible, keeping auth state stable');
      }
    });
  }

  isAuthenticated(): boolean {
    const now = Date.now();
    
    // Check if we have a cached result that's still valid
    if (this.authState && (now - this.authState.lastCheck) < this.CACHE_DURATION) {
      console.log('🔍 AuthStateManager: Using cached auth result:', this.authState.authenticated);
      return this.authState.authenticated;
    }

    // Perform fresh check
    const hasToken = !!localStorage.getItem('jwt_access_token');
    const hasUser = !!localStorage.getItem('jwt_user');
    const authenticated = hasToken && hasUser;

    // Cache the result
    this.authState = {
      authenticated,
      lastCheck: now
    };
    this.saveAuthState();

    console.log('🔍 AuthStateManager: Fresh auth check result:', authenticated);
    return authenticated;
  }

  setAuthenticated(value: boolean): void {
    this.authState = {
      authenticated: value,
      lastCheck: Date.now()
    };
    this.saveAuthState();
    console.log('🔍 AuthStateManager: Auth state set to:', value);
  }

  clearCache(): void {
    this.authState = null;
    sessionStorage.removeItem('auth_state_cache');
    console.log('🔍 AuthStateManager: Cache cleared');
  }
}

export const authStateManager = AuthStateManager.getInstance();